import { ERROR_TYPES, handleChatWebSocketError } from "./errors";

import { Content, RoleI, ROLES } from "@/types";
import { Message } from "@/components/Messaging/types";
import { ChatMessageI } from "@/components/Playground/types";
import { WEBSOCKET_MESSAGE_NAME } from "@/constants";
import { useUser } from "@/context/user-context";
import { usePlayground } from "@/components/Playground/playground-store";
import { useChatWidgetContext } from "@/components/Playground/Drawer/Chat/context";
import { useChannel } from "@/components/Messaging/useChannel";
import { useFetchIndividualRunRequest } from "@/queries";
import { getLatencyMs, getMessageId } from "@/components/Playground/utils";
import { handleSoftFailureIndividualRunRequest } from "@/utils/individualRun";

interface CreateNewMessagePayload {
  individual_run_request_id: number;
  payload: {
    message: {
      role: RoleI;
      content: Content[];
    };
  };
}

interface MessageUpdatePayload {
  individual_run_request_id: number;
  payload: {
    message: {
      role: RoleI;
      content: Content[];
    };
  };
}

interface RunCompletePayload {
  individual_run_request_id: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface RawResponseMessage extends Record<string, any> {
  role?: string;
  content?: Content[] | null;
  tool_calls?: unknown[];
  tool_call_id?: string;
  name?: string;
}

/**
 * Normalise `individual_run.message` (which may be a single object or an
 * array of objects) into a flat array of response messages.
 */
function normaliseResponseMessages(raw: unknown): RawResponseMessage[] {
  if (Array.isArray(raw)) {
    return raw as RawResponseMessage[];
  }
  if (raw && typeof raw === "object") {
    return [raw as RawResponseMessage];
  }
  return [];
}

/**
 * Build a `ChatMessageI` from a raw backend response message dict.
 */
function buildChatMessage(
  msg: RawResponseMessage,
  individualRunRequestId: number,
  index: number,
  extras: {
    finishedAt?: string;
    requestId?: number;
  },
): ChatMessageI {
  const role: RoleI = (msg.role as RoleI) ?? ROLES.ASSISTANT;
  const messageId = getMessageId(individualRunRequestId, role, index);

  const chatMsg: ChatMessageI = {
    id: messageId,
    role,
    content: Array.isArray(msg.content) ? msg.content : [],
    individual_run_request_id: individualRunRequestId,
    created_at: new Date().toISOString(),
    finished_at: extras.finishedAt,
    request_id: extras.requestId,
  };

  // Include tool_calls if present (for assistant messages)
  if (msg.tool_calls) {
    chatMsg.tool_calls = msg.tool_calls as ChatMessageI["tool_calls"];
  }

  // Include tool response fields
  if (role === ROLES.TOOL) {
    if (msg.tool_call_id) {
      chatMsg.tool_call_id = msg.tool_call_id;
    }
    if (msg.name) {
      chatMsg.name = msg.name;
    }
  }

  return chatMsg;
}

const useChatWebSocketListeners = () => {
  const { user } = useUser();
  const { chatStore } = usePlayground();
  const { variableSetIndex: widgetVariableSetIndex } = useChatWidgetContext();
  const { mutateAsync: fetchRequest } = useFetchIndividualRunRequest();

  const handleCreateNewMessage = (payload: Message) => {
    const {
      individual_run_request_id,
      payload: { message },
    }: CreateNewMessagePayload = JSON.parse(payload.data);

    if (!chatStore.isOwnRequest(individual_run_request_id)) {
      return;
    }

    // Resolve which variable set this request belongs to
    const variableSetIndex = chatStore.resolveRequest(individual_run_request_id);
    if (variableSetIndex === null) {
      return;
    }

    // Only process in the widget that owns this variable set
    if (variableSetIndex !== widgetVariableSetIndex) {
      return;
    }

    chatStore.recordSocketActivity();

    // Use index 0 for the first (streaming) response message.
    // The backend expects response message IDs in the format
    // `{request_id}_{role}_{index}` (e.g. `42_assistant_0`).
    const assistantMessage: ChatMessageI = {
      id: getMessageId(individual_run_request_id, message.role, 0),
      role: ROLES.ASSISTANT,
      content: Array.isArray(message.content) ? message.content : [],
      individual_run_request_id,
      created_at: new Date().toISOString(),
    };

    chatStore.addMessage(variableSetIndex, assistantMessage);
    chatStore.incrementTotalMessages(variableSetIndex);
    chatStore.setStreamingMessageId(variableSetIndex, assistantMessage.id);
  };

  const handleMessageUpdate = (payload: Message) => {
    const {
      individual_run_request_id,
      payload: { message },
    }: MessageUpdatePayload = JSON.parse(payload.data);

    if (!chatStore.isOwnRequest(individual_run_request_id)) {
      return;
    }

    // Only process in the widget that owns this variable set
    const variableSetIndex = chatStore.resolveRequest(individual_run_request_id);
    if (variableSetIndex !== widgetVariableSetIndex) {
      return;
    }

    chatStore.recordSocketActivity();

    // During streaming, we always update the first response message (index 0).
    const messageId = getMessageId(individual_run_request_id, message.role, 0);

    chatStore.updateMessage(messageId, {
      ...message,
      content: Array.isArray(message.content) ? message.content : [],
    });
  };

  const handleRunComplete = async (payload: Message) => {
    const { individual_run_request_id }: RunCompletePayload = JSON.parse(payload.data);

    if (!chatStore.isOwnRequest(individual_run_request_id)) {
      return;
    }

    // Resolve which variable set this request belongs to
    const variableSetIndex = chatStore.resolveRequest(individual_run_request_id);
    if (variableSetIndex === null) {
      return;
    }

    // Only process in the widget that owns this variable set
    if (variableSetIndex !== widgetVariableSetIndex) {
      return;
    }

    chatStore.recordSocketActivity();

    // The streaming placeholder message always uses index 0.
    const streamingMessageId = getMessageId(individual_run_request_id, ROLES.ASSISTANT, 0);

    try {
      const response = await fetchRequest(individual_run_request_id);
      const { individual_run } = response;

      if (individual_run?.status === "cancelled") {
        if (chatStore.getMessageById(streamingMessageId)) {
          chatStore.removeMessage(variableSetIndex, streamingMessageId);
          chatStore.decrementTotalMessages(variableSetIndex, 1);
        }
        chatStore.setPendingRequestId(variableSetIndex, null);
        chatStore.setStreamingMessageId(variableSetIndex, null);
        return;
      }

      const providerError = handleSoftFailureIndividualRunRequest(response);
      if (providerError) {
        handleChatWebSocketError({
          messageId: streamingMessageId,
          variableSetIndex,
          chatStore,
          errorType: ERROR_TYPES.PROVIDER_ERROR,
          response,
        });
        return;
      }

      const rawMessage = individual_run?.message;
      const stats = {
        cost: individual_run?.request?.price,
        input_tokens: individual_run?.request?.input_tokens,
        output_tokens: individual_run?.request?.output_tokens,
        latency_ms: getLatencyMs(individual_run?.created_at, individual_run?.finished_at),
      };

      // `rawMessage` can be either:
      //   - a single dict  (simple assistant reply, no tool calls)
      //   - an array of dicts (assistant + tool responses + final assistant)
      //
      // During streaming we created a single placeholder message with
      // id `{irr}_assistant_0`.  On completion we need to reconcile this
      // with the actual (possibly multi-message) response.
      const responseMessages = normaliseResponseMessages(rawMessage);

      if (responseMessages.length <= 1) {
        // ---- Single-message response (common case) ----
        // Just update the existing streaming placeholder in-place.
        const msg = responseMessages[0] ?? {};
        const built = buildChatMessage(msg, individual_run_request_id, 0, {
          finishedAt: individual_run?.finished_at,
          requestId: individual_run?.request_id,
        });

        // Update in-place (preserves any content streamed so far if the
        // fetched response happens to be empty due to a race).
        chatStore.updateMessage(streamingMessageId, {
          role: built.role,
          content: built.content,
          finished_at: built.finished_at,
          request_id: built.request_id,
          tool_calls: built.tool_calls,
          tool_call_id: built.tool_call_id,
          name: built.name,
        });
        chatStore.updateMessageStats(streamingMessageId, stats);
      } else {
        // ---- Multi-message response (tool-call turns) ----
        // Remove the single streaming placeholder and replace it with the
        // actual set of response messages, each with a correct composite ID.
        chatStore.removeMessage(variableSetIndex, streamingMessageId);
        // We already incremented total_messages by 1 for the placeholder;
        // decrement so we can add the correct count below.
        chatStore.decrementTotalMessages(variableSetIndex, 1);

        const extras = {
          finishedAt: individual_run?.finished_at,
          requestId: individual_run?.request_id,
        };

        for (let idx = 0; idx < responseMessages.length; idx++) {
          const chatMsg = buildChatMessage(
            responseMessages[idx],
            individual_run_request_id,
            idx,
            extras,
          );
          chatStore.addMessage(variableSetIndex, chatMsg);
        }

        chatStore.incrementTotalMessages(variableSetIndex, responseMessages.length);

        // Attach stats to the last response message (final assistant reply)
        const lastIdx = responseMessages.length - 1;
        const lastMsg = responseMessages[lastIdx];
        const lastRole: RoleI = (lastMsg.role as RoleI) ?? ROLES.ASSISTANT;
        const lastMessageId = getMessageId(individual_run_request_id, lastRole, lastIdx);
        chatStore.updateMessageStats(lastMessageId, stats);
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- error is not used
    } catch (error) {
      handleChatWebSocketError({
        messageId: streamingMessageId,
        variableSetIndex,
        chatStore,
        errorType: ERROR_TYPES.INTERNAL_SERVER_ERROR,
      });
    }

    chatStore.setPendingRequestId(variableSetIndex, null);
    chatStore.setStreamingMessageId(variableSetIndex, null);
  };

  useChannel(`user:${user.id}`, WEBSOCKET_MESSAGE_NAME.CREATE_NEW_MESSAGE, handleCreateNewMessage);
  useChannel(`user:${user.id}`, WEBSOCKET_MESSAGE_NAME.UPDATE_LAST_MESSAGE, handleMessageUpdate);
  useChannel(`user:${user.id}`, WEBSOCKET_MESSAGE_NAME.INDIVIDUAL_RUN_COMPLETE, handleRunComplete);
};

export default useChatWebSocketListeners;
